import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlag } from '~hooks';

import { ContainerHeader, TabContainer } from '~components';
import { faHandHoldingHeart } from '~components/icon';

import { ProdaConnectionSettings } from '../proda';

import Budgets from './budgets/Budgets';
import Contributions from './contributions/Contributions';
import ExcludedServiceTypes from './excluded-service-types/ExcludedServiceTypes';
import { PricePlans } from './price-plans';
import ServiceAssociations from './service-associations/ServiceAssociations';
import Services from './services/Services';
import StatementSettings from './statement-settings/StatementSettings';
import TimeBands from './time-bands/TimeBands';

const tabs = [
  { id: 'sah-client-services', title: 'SaH Approved Services', component: Services },
  { id: 'sah-client-contributions', title: 'SaH Contributions', component: Contributions },
  { id: 'sah-client-budgets', title: 'SaH Budgets', component: Budgets },
  { id: 'sah-service-associations', title: 'Service Type Assocs.', component: ServiceAssociations },
  { id: 'sah-price-plans', title: 'Price Plans', component: PricePlans },
  { id: 'sah-excluded-service-types', title: 'Excluded Service Types', component: ExcludedServiceTypes },
  { id: 'sah-statement-settings', title: 'Statement Settings', component: StatementSettings },
  { id: 'sah-time-bands', title: 'Time Bands', component: TimeBands },
];

// `?tab=` values map to tab indexes, so a page can deep-link straight to the tab that answers its
// problem instead of dropping the user on SaH Approved Services and leaving them to find it. Claim
// item errors use this (see support-at-home/lib/derive.ts). Unknown or missing -> the first tab,
// which is the behaviour this had before.
//
// These are URL surface: they are chosen to read well and to stay stable if the tab ORDER changes.
// Renaming one breaks existing links, so treat them as fixed once shipped.
const TAB_PARAMS = [
  'services',
  'contributions',
  'budgets',
  'associations',
  'price-plans',
  'excluded-service-types',
  'statement-settings',
  'time-bands',
];

const ConfigContainer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = Math.max(0, TAB_PARAMS.indexOf(searchParams.get('tab') ?? ''));

  // `replace` so paging through tabs does not fill the history stack - Back should leave the
  // configs screen, not walk back through every tab visited on the way.
  const handleTabChange = (index: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', TAB_PARAMS[index]);
    setSearchParams(next, { replace: true });
  };

  // Gated here rather than inside ProdaConnectionSettings: that component renders its "PRODA Not
  // Connected" status badge outside its own authTag guard (only the settings cog is auth-gated),
  // so every Support at Home viewer — not just admins — sees it. Gating one level up removes the
  // cog and the badge together in one place, and keeps the flag concern out of the PRODA component
  // itself (TAV-2589).
  const { isEnabled: isProdaDirectEnabled, isResolved: isProdaFlagResolved } = useFeatureFlag('feature-proda-direct');

  return (
    <div className="p-2">
      <ContainerHeader icon={faHandHoldingHeart} iconSize="1x" className="info" dataCy="support-at-home-configs-header">
        <div className="d-flex align-items-center gap-2">
          <span>Support At Home</span>
          {/* Render nothing until the flag resolves — the flag check is async, so rendering
              ProdaConnectionSettings eagerly (even behind a loading state) would flash the red
              "PRODA Not Connected" badge to every customer for a moment before it disappears,
              which is the exact bug this flag exists to prevent. */}
          {isProdaFlagResolved && isProdaDirectEnabled && <ProdaConnectionSettings />}
        </div>
      </ContainerHeader>
      <TabContainer tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
};

export default ConfigContainer;
